import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const LEGAL_NAV = [
  { to: "/company", label: "Company" },
  { to: "/contact", label: "Contact" },
  { to: "/terms", label: "Terms" },
  { to: "/privacy", label: "Privacy" },
  { to: "/cookies", label: "Cookies" },
  { to: "/refunds", label: "Refunds" },
  { to: "/shipping", label: "Shipping" },
];

/**
 * Shared premium layout for legal & corporate document pages.
 * Apple/Stripe-grade reading experience: readable measure, generous
 * spacing, calm hierarchy, consistent corporate sign-off.
 */
export const LegalLayout = ({ title, updated, kicker = "Legal", children, pageTestId }) => {
  useEffect(() => {
    const prev = document.title;
    document.title = `${title} · Ivory Pulse`;
    window.scrollTo({ top: 0, behavior: "instant" });
    return () => {
      document.title = prev;
    };
  }, [title]);

  return (
    <div
      className="min-h-screen bg-[#050505] text-white"
      data-testid={pageTestId || "legal-page"}
    >
      {/* Simple top bar */}
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#050505]/75 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-6 md:px-10">
          <Link
            to="/"
            data-testid="legal-back-home"
            className="flex items-center gap-3 text-sm text-white/70 transition-colors hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            <img
              src={`${process.env.PUBLIC_URL}/logo.png`}
              alt="Ivory Pulse"
              draggable={false}
              className="brand-invert h-7 w-auto"
            />
          </Link>
          <Link
            to="/"
            data-testid="legal-home-link"
            className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/45 transition-colors hover:text-white"
          >
            Back to Ivory Pulse
          </Link>
        </div>
      </header>

      {/* Article */}
      <article className="mx-auto max-w-3xl px-6 pt-16 pb-24 md:px-10 md:pt-24 md:pb-32">
        <div className="flex items-center gap-3">
          <span className="h-px w-8 bg-[#C7A962]" />
          <span className="font-mono text-[10.5px] uppercase tracking-[0.26em] text-[#C7A962]">
            {kicker}
          </span>
        </div>
        <h1
          className="font-display mt-6 text-4xl font-medium leading-[1.05] tracking-[-0.03em] md:text-5xl"
          data-testid="legal-title"
        >
          {title}
        </h1>
        <p
          className="mt-4 font-mono text-[11px] uppercase tracking-[0.16em] text-white/35"
          data-testid="legal-last-updated"
        >
          Last updated · {updated}
        </p>

        <div
          className="font-body mt-14 space-y-14 text-[15px] leading-[1.85] text-white/70 md:mt-16 md:text-base"
          data-testid="legal-content"
        >
          {children}
        </div>

        <p className="mt-16 border-t border-white/10 pt-8 text-xs leading-relaxed text-white/40">
          This document is written in plain English for transparency. It is
          not legal advice. Where new terms apply to payments or deliveries,
          updates are published on this page in advance.
        </p>
      </article>

      {/* Legal cross-nav + company sign-off */}
      <footer className="border-t border-white/10">
        <div className="mx-auto flex max-w-5xl flex-col items-center justify-between gap-6 px-6 py-10 md:flex-row md:px-10">
          <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-white/55">
            {LEGAL_NAV.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`legal-nav-${l.label.toLowerCase()}`}
                className="transition-colors hover:text-white"
              >
                {l.label}
              </Link>
            ))}
          </nav>
          <p className="text-center text-[11px] text-white/40 md:text-right">
            IVORY PULSE SRL · Reg. (CUI) 52619680 · contact@ivory-pulse.com
          </p>
        </div>
      </footer>
    </div>
  );
};

// Shared helpers for prose inside legal pages — keeps visual consistency.
export const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-24">
    <h2 className="font-display mb-5 text-xl font-medium tracking-[-0.02em] text-white md:text-2xl">
      {title}
    </h2>
    <div className="space-y-4 text-white/70">{children}</div>
  </section>
);

export const SubList = ({ items }) => (
  <ul className="list-disc space-y-2.5 pl-5 text-white/65 marker:text-[#C7A962]/50">
    {items.map((t, i) => (
      <li key={i}>{t}</li>
    ))}
  </ul>
);
